import { Device } from '@twilio/voice-sdk';
import axios from 'axios';

// Estado global del cliente
let device = null;
let conexionActual = null;
let estadoDevice = 'disconnected'; // 'disconnected', 'connecting', 'ready', 'error'

// Configuración
const CONFIG = {
  tokenUrl: 'https://academicokpi.club/kpi/twilio.token',
  debug: process.env.NODE_ENV === 'development',
  timeout: 10000, // 10 segundos timeout
  reintentos: 3
};

// Eventos personalizados para el componente Vue
const eventBus = {
  callbacks: {},
  on(evento, callback) {
    if (!this.callbacks[evento]) {
      this.callbacks[evento] = [];
    }
    this.callbacks[evento].push(callback);
  },
  off(evento, callback) {
    if (this.callbacks[evento]) {
      this.callbacks[evento] = this.callbacks[evento].filter(cb => cb !== callback);
    }
  },
  emit(evento, data) {
    if (this.callbacks[evento]) {
      this.callbacks[evento].forEach(callback => callback(data));
    }
  }
};

/**
 * Inicializar el cliente Twilio
 * @returns {Promise} Promesa que se resuelve cuando el device está listo
 */
export async function inicializarTwilio() {
  try {
    estadoDevice = 'connecting';
    eventBus.emit('estadoCambiado', { estado: 'connecting', mensaje: 'Conectando...' });

    // Si ya existe un device, limpiarlo
    if (device) {
      await limpiarDevice();
    }

    // Obtener token con timeout y reintentos
    const token = await obtenerTokenConReintentos();
    
    // Crear nuevo device
    device = new Device(token, {
      debug: CONFIG.debug,
      allowIncomingWhileBusy: false,
      codecPreferences: ['opus', 'pcmu']
    });

    // Configurar eventos del device
    configurarEventosDevice();

    // Esperar a que esté listo
    await esperarDeviceListo();
    
    estadoDevice = 'ready';
    eventBus.emit('estadoCambiado', { estado: 'ready', mensaje: 'Listo para llamar' });
    
    return device;
    
  } catch (error) {
    estadoDevice = 'error';
    eventBus.emit('estadoCambiado', { estado: 'error', mensaje: error.message });
    throw new Error(`Error al inicializar Twilio: ${error.message}`);
  }
}

/**
 * Realizar una llamada
 * @param {string} numeroDestino - Número a llamar (formato: +521XXXXXXXXXX)
 * @returns {Promise} Promesa que se resuelve cuando la llamada se conecta
 */
export async function llamarANumero(numeroDestino) {
  try {
    // Validaciones
    if (!numeroDestino) {
      throw new Error('Número de destino es requerido');
    }
    
    if (!validarNumero(numeroDestino)) {
      throw new Error('Formato de número inválido');
    }
    
    if (!device || estadoDevice !== 'ready') {
      throw new Error('Twilio Device no está listo. Inicializa primero.');
    }
    
    if (conexionActual && conexionActual.status() !== 'closed') {
      throw new Error('Ya hay una llamada en curso');
    }

    // Iniciar llamada
    eventBus.emit('llamadaIniciando', { numero: numeroDestino });
    
    conexionActual = device.connect({
      params: { To: numeroDestino },
      rtcConstraints: {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      }
    });

    // Configurar eventos de la conexión
    configurarEventosConexion(conexionActual, numeroDestino);
    
    return conexionActual;
    
  } catch (error) {
    eventBus.emit('errorLlamada', { error: error.message });
    throw error;
  }
}

/**
 * Colgar la llamada actual
 * @returns {Promise} Promesa que se resuelve cuando se cuelga
 */
export async function colgarLlamada() {
  try {
    if (!device) {
      throw new Error('Device no inicializado');
    }

    if (conexionActual && conexionActual.status() !== 'closed') {
      eventBus.emit('llamadaColgando', {});
      conexionActual.disconnect();
    } else {
      // Por si acaso, desconectar todas
      device.disconnectAll();
    }
    
    conexionActual = null;
    
  } catch (error) {
    eventBus.emit('errorLlamada', { error: error.message });
    throw error;
  }
}

/**
 * Obtener el estado actual del device
 * @returns {string} Estado actual
 */
export function obtenerEstado() {
  return {
    device: estadoDevice,
    conexion: conexionActual ? conexionActual.status() : null,
    enLlamada: conexionActual && conexionActual.status() === 'open'
  };
}

/**
 * Suscribirse a eventos del cliente
 * @param {string} evento - Nombre del evento
 * @param {Function} callback - Función callback
 */
export function suscribirseAEventos(evento, callback) {
  eventBus.on(evento, callback);
}

/**
 * Desuscribirse de eventos
 * @param {string} evento - Nombre del evento
 * @param {Function} callback - Función callback
 */
export function desuscribirseDeEventos(evento, callback) {
  eventBus.off(evento, callback);
}

/**
 * Limpiar recursos
 */
export async function limpiarRecursos() {
  try {
    if (conexionActual) {
      conexionActual.disconnect();
    }
    
    if (device) {
      device.disconnectAll();
      device.destroy();
    }
    
    device = null;
    conexionActual = null;
    estadoDevice = 'disconnected';
    
  } catch (error) {
    console.error('Error al limpiar recursos:', error);
  }
}

// === FUNCIONES AUXILIARES ===

async function obtenerTokenConReintentos() {
  let ultimoError;
  
  for (let intento = 1; intento <= CONFIG.reintentos; intento++) {
    try {
      const response = await axios.get(CONFIG.tokenUrl, {
        timeout: CONFIG.timeout,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // if (!response.data?.token) {
      //   throw new Error('Token no encontrado en la respuesta');
      // }
      
      return response.data.token;
      
    } catch (error) {
      ultimoError = error;
      console.warn(`Intento ${intento} fallido:`, error.message);
      
      if (intento < CONFIG.reintentos) {
        await esperar(1000 * intento); // Backoff exponencial
      }
    }
  }
  
  throw new Error(`No se pudo obtener el token después de ${CONFIG.reintentos} intentos: ${ultimoError.message}`);
}

function configurarEventosDevice() {
  device.on('ready', () => {
    console.log('✅ Twilio Device listo (evento ready)');
  });

  device.on('error', (error) => {
    console.error('❌ Error en Twilio Device:', error);
    console.error('⛔ Estado del device en error:', device.state);
    estadoDevice = 'error';
    eventBus.emit('estadoCambiado', { estado: 'error', mensaje: error.message });
  });

  device.on('offline', () => {
    console.warn('📴 Device offline (evento offline)');
    console.warn('📡 Estado actual en offline:', device.state);
    estadoDevice = 'disconnected';
    eventBus.emit('estadoCambiado', { estado: 'disconnected', mensaje: 'Desconectado' });
  });

  device.on('incoming', (connection) => {
    console.log('📞 Llamada entrante:', connection.parameters.From);
    eventBus.emit('llamadaEntrante', { de: connection.parameters.From });
  });

  console.log('📡 Eventos configurados, estado inicial:', device.state);
}

function configurarEventosConexion(connection, numeroDestino) {
  connection.on('accept', () => {
    console.log('✅ Conectado con', numeroDestino);
    eventBus.emit('llamadaConectada', { numero: numeroDestino });
  });

  connection.on('disconnect', () => {
    console.log('🔚 Llamada finalizada');
    conexionActual = null;
    eventBus.emit('llamadaFinalizada', { numero: numeroDestino });
  });

  connection.on('error', (error) => {
    console.error('❌ Error en la llamada:', error);
    conexionActual = null;
    eventBus.emit('errorLlamada', { error: error.message, numero: numeroDestino });
  });

  connection.on('ringing', () => {
    console.log('📞 Timbrando...');
    eventBus.emit('llamadaTimbrando', { numero: numeroDestino });
  });

  connection.on('cancel', () => {
    console.log('🚫 Llamada cancelada');
    conexionActual = null;
    eventBus.emit('llamadaCancelada', { numero: numeroDestino });
  });
}

async function esperarDeviceListo() {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      console.error('⏱️ Timeout esperando device listo (state: ' + device.state + ')');
      reject(new Error('Timeout esperando device listo'));
    }, CONFIG.timeout);

    const checker = setInterval(() => {
      console.log('🔄 Estado actual del device:', device.state);
    }, 1000);

    if (device.state === 'ready') {
      clearTimeout(timeout);
      clearInterval(checker);
      resolve();
      return;
    }

    const onReady = () => {
      console.log('✅ Device dijo "ready"');
      clearTimeout(timeout);
      clearInterval(checker);
      device.removeListener('error', onError);
      resolve();
    };

    const onError = (error) => {
      console.error('❌ Device lanzó error ANTES de ready:', error);
      clearTimeout(timeout);
      clearInterval(checker);
      device.removeListener('ready', onReady);
      reject(error);
    };

    device.once('ready', onReady);
    device.once('error', onError);
  });
}

function validarNumero(numero) {
  // Validar formato +521XXXXXXXXXX
  const regex = /^\+521\d{10}$/;
  return regex.test(numero);
}

async function limpiarDevice() {
  if (device) {
    try {
      if (conexionActual) {
        conexionActual.disconnect();
      }
      device.disconnectAll();
      device.destroy();
    } catch (error) {
      console.warn('Error al limpiar device anterior:', error);
    }
  }
  device = null;
  conexionActual = null;
}

function esperar(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Limpiar recursos al cerrar la ventana
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', limpiarRecursos);
}